<template>
	<div class="sk-widget residential-suborder-widget">
		<div class="sk-widget-header">
			<div class="residential-suborder-date">
				{{ formattedDate }}
				<span>at</span>
				{{ formattedTime }}
			</div>
			<div>
				<span v-if="!isMobile && hasCoupon" class="pill pill-grey">
					<i class="sk-icon-tag-solid"></i>
					Coupon Applied
				</span>
				<button
					v-if="order.canReorder"
					class="button button-secondary-dark button-auto"
					@click="reorder"
				>
					<i class="sk-icon-redo-alt-regular"></i>
					Reorder
				</button>
				<router-link
					v-if="isOngoing"
					tag="button"
					class="button button-primary button-auto"
					:to="`/${order.business.slug}/track/${order.id}`"
				>
					<i class="sk-icon-map-marker-alt-solid"></i>
					Track
				</router-link>
			</div>
		</div>
		<div class="sk-widget-line-item residential-suborder-line-item">
			<div>
				<div>Meal</div>
				<div class="ellipsify-text">{{ order.menuName }}</div>
			</div>
			<div>
				<div>Items</div>
				<div>{{ itemQuantity }}</div>
			</div>
			<div>
				<div>Price</div>
				<div>{{ order.price | currency }}</div>
			</div>
			<button
				@click="expandSuborder"
				class="button button-transparent button-auto"
			>
				<i class="sk-icon-chevron-down"></i>
			</button>
		</div>
		<transition-expand>
			<div v-if="expanded">
				<CheckoutItem
					v-for="item in products"
					:key="item.id"
					:item="item"
					:editable="false"
					:showRestaurantName="false"
					:showPrice="false"
				/>
				<div class="pricing-line-item" v-if="hasCoupon">
					<div>Money Saved</div>
					<div class="pricing-discount">
						-{{ discount | currency }}
					</div>
				</div>
				<div class="pricing-line-item">
					<div>Neighborhood Delivery</div>
					<div>FREE</div>
				</div>
				<div class="pricing-line-item">
					<div>Taxes & Fees</div>
					<div>{{ fees | currency }}</div>
				</div>
				<div class="pricing-line-item">
					<div>Tip</div>
					<div>{{ order.tip | currency }}</div>
				</div>
				<div class="pricing-line-item">
					<b>Total</b>
					<b>{{ order.price | currency }}</b>
				</div>
			</div>
		</transition-expand>
	</div>
</template>

<style scoped>
.residential-suborder-date span {
	color: var(--sk-grey3);
	font-weight: 400;
}

.sk-widget-header i {
	font-size: 10px;
	margin-right: 10px;
}

.sk-widget-header .button,
.sk-widget-header .pill {
	font-size: 10px;
	font-weight: 400;
	padding: 5px 15px;
}

.sk-widget-header .pill + .button,
.sk-widget-header .button + .button {
	margin-left: 20px;
}

.residential-suborder-widget >>> .checkout-item {
	padding: 15px;
}

.residential-suborder-widget >>> .checkout-item + .pricing-line-item {
	padding-top: 15px;
	border-top: 1px solid var(--gR-white2);
}

.pricing-line-item {
	display: flex;
	justify-content: space-between;
	font-size: 10px;
	padding: 0 15px 15px 15px;
}

.pricing-line-item > div:not(.pricing-discount) {
	color: var(--sk-grey3);
}

.pricing-line-item > b {
	color: var(--gR-base-80);
}

.pricing-discount {
	color: var(--gR-teal-70);
}
</style>

<script>
import moment from "moment"
import utils from "@/utils"
import { eventBus } from "@/eventBus"
import { EventBusEvents } from "@/enums/EventBusEvents"
import { NotificationType } from "@/enums/NotificationType"
import mobileResponsivenessMixin from "@/mixins/mobile-responsiveness-mixin"
import currencyFilterMixin from "@/mixins/currency-filter-mixin"
import TransitionExpand from "@/components/common/TransitionExpand.vue"
import CheckoutItem from "@/components/checkout/CheckoutItem.vue"

export default {
	name: "UserSuborder",
	mixins: [mobileResponsivenessMixin, currencyFilterMixin],
	components: {
		TransitionExpand,
		CheckoutItem
	},
	data: function () {
		return {
			expanded: false
		}
	},
	props: {
		order: Object
	},
	computed: {
		suborder() {
			return this.order.sub_orders[0]
		},
		products() {
			return this.suborder.products.map((product) => {
				let options = []
				if (product.metas) {
					options = product.metas.reduce((options, meta) => {
						if (!options[meta.key]) {
							options[meta.key] = []
						}
						options[meta.key].push({
							name: meta.value,
							id: `${meta.meta_id}-${meta.meta_val_id}`
						})
						return options
					}, {})
				}
				return {
					name: product.name,
					options: options,
					comments: product.comments,
					count: product.quantity,
					price: product.subtotal,
					id: product.id
				}
			})
		},
		deliverySlot() {
			if (this.suborder.delivery_slot_local) {
				return moment
					.utc(this.suborder.delivery_slot_local)
					.format("YYYY-MM-DD HH:mm:ss")
			}
			return null
		},
		formattedDate() {
			if (this.deliverySlot) {
				return moment(this.deliverySlot, "YYYY-MM-DD HH:mm:ss").format(
					"dddd, MMMM D"
				)
			} else {
				return moment(this.order.created_time_local).format(
					"dddd, MMMM D"
				)
			}
		},
		formattedTime() {
			if (this.deliverySlot) {
				return moment(this.deliverySlot, "YYYY-MM-DD HH:mm:ss").format(
					"h a"
				)
			} else {
				return "ASAP"
			}
		},
		hasCoupon() {
			return this.order.coupon_used == 1 && this.discount > 0
		},
		isOngoing() {
			return (
				this.suborder.delivered_time == undefined ||
				this.suborder.delivered_time == null
			)
		},
		itemQuantity() {
			return this.products.reduce(
				(total, { quantity }) => total + quantity,
				0
			)
		},
		fees() {
			const business = this.$store.state.business
			const fees = business.service_fee / 100 + business.tax / 100
			return (this.subtotal * fees).toFixed(2)
		},
		subtotal() {
			let price = 0
			if (this.products && this.products.length > 0) {
				price = this.products.reduce(
					(total, { price }) => total + price,
					0
				)
			}
			return price
		},
		discount() {
			if (this.order.coupon_uses && this.order.coupon_uses.length > 0) {
				return this.order.coupon_uses.reduce(
					(total, { amount }) => total + amount,
					0
				)
			}
			return 0
		}
	},
	methods: {
		expandSuborder() {
			this.expanded = !this.expanded
		},
		async reorder() {
			try {
				this.$emit("toggleLoader", true)
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Reorder Clicked",
					data: {
						order_id: this.order.id
					}
				})
				const validOrders = await this.$store.dispatch(
					"validateOrdersReorder",
					[this.order]
				)
				if (validOrders.includes(this.order.id)) {
					const itemsInCartCount =
						this.$store.getters.itemsInCartCount
					if (itemsInCartCount > 0) {
						this.$emit("toggleLoader", false)
						let promise = utils.createPromise()
						this.$emit("confirmClear", promise)
						const confirmed = await promise.promise
						if (!confirmed) {
							this.$store.dispatch("sendMixpanelEvent", {
								msg: "Reorder Cart Clear Denied"
							})
							return false
						}
						this.$store.dispatch("clearCart")
					}
					this.$emit("toggleLoader", true)
					const addedToCart = await this.$store.dispatch(
						"reorderOrder",
						this.order
					)
					this.$emit("toggleLoader", false)
					if (addedToCart) {
						this.$router.push({
							path: `/${this.order.business.slug}/checkout`
						})
					} else {
						eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
							message: "Order is no longer valid for reordering",
							type: NotificationType.ERROR
						})
						this.$store.dispatch("updateOrderReorder", {
							order: this.order,
							canReorder: false
						})
					}
				} else {
					this.$emit("toggleLoader", false)
					eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
						message: "Order is no longer valid for reordering",
						type: NotificationType.ERROR
					})
					this.$store.dispatch("updateOrderReorder", {
						order: this.order,
						canReorder: false
					})
				}
			} catch (e) {
				this.$emit("toggleLoader", false)
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message: "Something went wrong, please try again.",
					type: NotificationType.ERROR
				})
				throw e
			}
		}
	}
}
</script>